import { useRef, useState } from 'react'
import { processDataColumnResponse } from '../../../../../_common/functions'

export type DraftPreview = { status: 'loading' } | { status: 'ready'; rowData: any[]; columnDefs: any[]; totalRows: number } | { status: 'empty' } | { status: 'error' }

type PreviewRequest = (payload: any, onSuccess: (resp: any) => void, onError: (error: any) => void) => void

const toDraftPreview = (resp: any): DraftPreview => {
    const result = resp?.data?.result
    if (!Array.isArray(result) || result.length === 0) return { status: 'empty' }
    let rowData: any[] = []
    let columnDefs: any[] = []
    processDataColumnResponse(
        null,
        result,
        (defs: any[]) => (columnDefs = defs),
        (rows: any[]) => (rowData = rows),
        () => {},
        () => {},
    )
    if (rowData.length === 0) return { status: 'empty' }
    return { status: 'ready', rowData, columnDefs, totalRows: Number(resp?.data?.total ?? result.length) }
}

/**
 * Rows of a proposed (not yet applied) chat filter. Kept apart from the page's own preview state, so a draft can never
 * change what Next Step saves; any newer page preview or draft supersedes an in-flight one.
 */
export const useDraftPreview = (request: PreviewRequest) => {
    const [draft, setDraft] = useState<DraftPreview | null>(null)
    const requestIdRef = useRef(0)
    const requestRef = useRef(request)
    requestRef.current = request

    const clearDraft = () => {
        requestIdRef.current += 1
        setDraft(null)
    }

    const previewDraft = (payload: any) => {
        const id = ++requestIdRef.current
        setDraft({ status: 'loading' })
        requestRef.current(
            payload,
            (resp) => {
                if (id === requestIdRef.current) setDraft(toDraftPreview(resp))
            },
            () => {
                if (id === requestIdRef.current) setDraft({ status: 'error' })
            },
        )
    }

    // The grid shows the draft's rows only once they are ready; while loading or after an error it keeps the current rows.
    const displayRows = (current: any) => {
        if (draft?.status === 'ready') return draft.rowData
        return draft?.status === 'empty' ? [] : current
    }
    const displayColumns = (current: any) => (draft?.status === 'ready' ? draft.columnDefs : current)

    return { draft, previewDraft, clearDraft, displayRows, displayColumns }
}
